import { useState, useEffect, useCallback } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, Loader2, Edit, FileText, Users, ClipboardList,
  ChevronDown, ChevronUp, GripVertical, CheckCircle2, Clock, Send, X, Eye, Upload, Sparkles
} from "lucide-react";
import { API } from "@/lib/api";

const FIELD_TYPES = [
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
  { value: "select", label: "Dropdown" },
  { value: "radio", label: "Radio Buttons" },
  { value: "checkbox", label: "Checkbox" },
  { value: "date", label: "Date" },
  { value: "number", label: "Number" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
];

const FORM_TYPES = [
  { value: "agreement", label: "Agreement" },
  { value: "intake", label: "Intake Form" },
  { value: "form", label: "General Form" },
];

export default function AdminForms() {
  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTemplate, setEditTemplate] = useState(null);
  const [viewAssignment, setViewAssignment] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a, c] = await Promise.all([
        http.get("/forms/admin/templates"),
        http.get("/forms/admin/assignments"),
        http.get("/client/admin/clients"),
      ]);
      setTemplates(t.data);
      setAssignments(a.data);
      setClients(c.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  const tabs = [
    { key: "templates", label: "Templates", icon: <FileText size={14} />, count: templates.length },
    { key: "assignments", label: "Assigned Forms", icon: <Send size={14} />, count: assignments.filter(a => a.status !== "completed").length },
    { key: "submissions", label: "Submissions", icon: <CheckCircle2 size={14} />, count: assignments.filter(a => a.status === "completed").length },
  ];

  return (
    <div className="space-y-6" data-testid="admin-forms">
      <h2 className="font-display text-3xl text-[#F4F1EA]">Agreements & Forms</h2>

      <div className="flex gap-2 flex-wrap">
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setEditTemplate(null); setViewAssignment(null); }}
            data-testid={`forms-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium tracking-wide transition-all ${
              tab === t.key ? "bg-[#C9A055] text-[#1A1A1A]" : "border border-white/10 text-zinc-400 hover:border-[#C9A055]/40"
            }`}>
            {t.icon} {t.label} {t.count !== undefined && <span className="ml-1 bg-black/20 px-1.5 py-0.5 rounded-full text-[10px]">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "templates" && !editTemplate && (
        <TemplatesList templates={templates} onEdit={setEditTemplate} onRefresh={load} />
      )}
      {tab === "templates" && editTemplate && (
        <TemplateEditor template={editTemplate} onClose={() => setEditTemplate(null)} onSaved={() => { setEditTemplate(null); load(); }} />
      )}
      {tab === "assignments" && !viewAssignment && (
        <AssignmentsList
          assignments={assignments.filter(a => a.status !== "completed")}
          templates={templates}
          clients={clients}
          onRefresh={load}
          onView={setViewAssignment}
        />
      )}
      {tab === "submissions" && !viewAssignment && (
        <AssignmentsList
          assignments={assignments.filter(a => a.status === "completed")}
          templates={templates}
          clients={clients}
          onRefresh={load}
          onView={setViewAssignment}
          readOnly
        />
      )}
      {viewAssignment && (
        <SubmissionViewer assignment={viewAssignment} onClose={() => setViewAssignment(null)} />
      )}
    </div>
  );
}

/* ═══ TEMPLATES LIST ═══ */
function TemplatesList({ templates, onEdit, onRefresh }) {
  const [converting, setConverting] = useState(false);
  const fileRef = { current: null };

  const create = () => onEdit({
    id: null,
    title: "",
    description: "",
    type: "agreement",
    fields: [],
    requires_signature: true,
    acknowledgment_text: "I understand and acknowledge the terms described above.",
    active: true,
  });

  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await http.delete(`/forms/admin/templates/${id}`);
      toast.success("Deleted");
      onRefresh();
    } catch { toast.error("Failed"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setConverting(true);
    toast.info("Analyzing document with AI... This may take a moment.");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem("stc_token");
      const resp = await fetch(`${API}/forms/admin/convert`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.detail || "Upload failed");
      }

      if (!data.ok) {
        toast.error(data.error || "AI could not parse the document");
        return;
      }

      toast.success(`Extracted ${data.field_count} fields from document`);

      // Open editor pre-populated with AI-extracted fields
      onEdit({
        id: null,
        title: data.title || "Untitled Form",
        description: data.description || "",
        type: data.type || "form",
        fields: data.fields || [],
        requires_signature: data.requires_signature || false,
        acknowledgment_text: data.acknowledgment_text || "",
        active: true,
      });
    } catch (err) {
      toast.error(err.message || "Failed to analyze document");
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-3">
        <p className="text-zinc-400 text-sm">{templates.length} template{templates.length !== 1 ? "s" : ""}</p>
        <div className="flex gap-2">
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*" className="hidden"
            ref={el => fileRef.current = el} onChange={handleUpload} data-testid="convert-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={converting} data-testid="upload-convert-btn"
            className="flex items-center gap-2 border border-[#C9A055] text-[#C9A055] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#C9A055] hover:text-[#1A1A1A] transition-all disabled:opacity-50">
            {converting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {converting ? "Analyzing..." : "Upload & Convert"}
          </button>
          <button onClick={create} data-testid="create-template-btn"
            className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37] transition-all">
            <Plus size={14} /> New Template
          </button>
        </div>
      </div>
      {templates.length === 0 && (
        <div className="text-center py-16 text-zinc-500">
          <FileText size={40} className="mx-auto mb-4 opacity-30" />
          <p>No templates yet. Create one to get started.</p>
        </div>
      )}
      <div className="space-y-3">
        {templates.map(t => (
          <div key={t.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 flex items-center justify-between gap-4"
            data-testid={`template-${t.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  t.type === "agreement" ? "bg-amber-900/30 text-amber-400" :
                  t.type === "intake" ? "bg-blue-900/30 text-blue-400" :
                  "bg-zinc-800 text-zinc-400"
                }`}>{t.type}</span>
                {!t.active && <span className="text-[10px] text-red-400 uppercase">Inactive</span>}
              </div>
              <h3 className="text-[#F4F1EA] font-medium truncate">{t.title}</h3>
              <p className="text-zinc-500 text-xs mt-1 truncate">{t.description || "No description"}</p>
              <p className="text-zinc-600 text-[10px] mt-1">
                {t.fields?.length || 0} field{(t.fields?.length || 0) !== 1 ? "s" : ""}
                {t.requires_signature && " · Signature required"}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => onEdit(t)} className="text-zinc-500 hover:text-[#C9A055] transition-colors p-2" data-testid={`edit-template-${t.id}`}>
                <Edit size={14} />
              </button>
              <button onClick={() => del(t.id)} className="text-zinc-500 hover:text-red-400 transition-colors p-2" data-testid={`delete-template-${t.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ TEMPLATE EDITOR ═══ */
function TemplateEditor({ template, onClose, onSaved }) {
  const [form, setForm] = useState({ ...template });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const addField = () => {
    const newField = { id: crypto.randomUUID().slice(0, 8), type: "text", label: "", required: false, placeholder: "", options: [], helper_text: "" };
    update("fields", [...(form.fields || []), newField]);
  };

  const updateField = (idx, key, val) => {
    const fields = [...(form.fields || [])];
    fields[idx] = { ...fields[idx], [key]: val };
    update("fields", fields);
  };

  const removeField = (idx) => {
    update("fields", (form.fields || []).filter((_, i) => i !== idx));
  };

  const moveField = (idx, dir) => {
    const fields = [...(form.fields || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fields.length) return;
    [fields[idx], fields[newIdx]] = [fields[newIdx], fields[idx]];
    update("fields", fields);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (form.id) {
        await http.patch(`/forms/admin/templates/${form.id}`, form);
      } else {
        await http.post("/forms/admin/templates", form);
      }
      toast.success("Template saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="template-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-[#F4F1EA] text-lg font-medium">{form.id ? "Edit" : "Create"} Template</h3>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300 transition-colors"><X size={18} /></button>
      </div>

      <div className="space-y-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
        <div>
          <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Title *</label>
          <input value={form.title} onChange={e => update("title", e.target.value)}
            className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-[#F4F1EA] text-sm focus:border-[#C9A055] focus:outline-none"
            data-testid="template-title" placeholder="e.g. Client Intake Agreement" />
        </div>
        <div>
          <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Description</label>
          <textarea value={form.description} onChange={e => update("description", e.target.value)} rows={2}
            className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-[#F4F1EA] text-sm focus:border-[#C9A055] focus:outline-none resize-none"
            data-testid="template-description" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Type</label>
            <select value={form.type} onChange={e => update("type", e.target.value)}
              className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 text-[#F4F1EA] text-sm focus:border-[#C9A055] focus:outline-none"
              data-testid="template-type">
              {FORM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={e => update("active", e.target.checked)}
                className="accent-[#C9A055]" />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={form.requires_signature} onChange={e => update("requires_signature", e.target.checked)}
                className="accent-[#C9A055]" data-testid="template-requires-signature" />
              Requires Signature
            </label>
          </div>
        </div>
        {form.requires_signature && (
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Acknowledgment Text</label>
            <textarea value={form.acknowledgment_text} onChange={e => update("acknowledgment_text", e.target.value)} rows={3}
              className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-[#F4F1EA] text-sm focus:border-[#C9A055] focus:outline-none resize-none"
              data-testid="template-ack-text" />
          </div>
        )}
      </div>

      {/* Fields Builder */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-[#F4F1EA] text-sm font-medium">Form Fields ({(form.fields || []).length})</h4>
          <button onClick={addField} data-testid="add-field-btn"
            className="flex items-center gap-1 text-[#C9A055] text-xs hover:text-[#D4AF37] transition-colors">
            <Plus size={14} /> Add Field
          </button>
        </div>

        {(form.fields || []).length === 0 && (
          <p className="text-zinc-600 text-sm text-center py-4">No fields yet. Click "Add Field" to start building.</p>
        )}

        <div className="space-y-3">
          {(form.fields || []).map((field, idx) => (
            <FieldEditor key={field.id || idx} field={field} idx={idx}
              onUpdate={(k, v) => updateField(idx, k, v)}
              onRemove={() => removeField(idx)}
              onMove={(dir) => moveField(idx, dir)}
              isFirst={idx === 0}
              isLast={idx === (form.fields || []).length - 1}
            />
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button onClick={save} disabled={saving} data-testid="save-template-btn"
          className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#D4AF37] transition-all disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving..." : "Save Template"}
        </button>
        <button onClick={onClose} className="px-6 py-2.5 rounded-lg text-sm border border-white/10 text-zinc-400 hover:text-zinc-200 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ═══ FIELD EDITOR ROW ═══ */
function FieldEditor({ field, idx, onUpdate, onRemove, onMove, isFirst, isLast }) {
  const [expanded, setExpanded] = useState(!field.label);
  const needsOptions = ["select", "radio", "checkbox"].includes(field.type);

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] overflow-hidden" data-testid={`field-${idx}`}>
      <div className="flex items-center gap-2 px-4 py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex flex-col gap-0.5">
          {!isFirst && <button onClick={e => { e.stopPropagation(); onMove(-1); }} className="text-zinc-600 hover:text-zinc-300"><ChevronUp size={12} /></button>}
          {!isLast && <button onClick={e => { e.stopPropagation(); onMove(1); }} className="text-zinc-600 hover:text-zinc-300"><ChevronDown size={12} /></button>}
        </div>
        <span className="text-zinc-500 text-[10px] uppercase tracking-wider w-16">{field.type}</span>
        <span className="text-[#F4F1EA] text-sm flex-1 truncate">{field.label || "(untitled)"}</span>
        {field.required && <span className="text-amber-500 text-[10px]">Required</span>}
        <button onClick={e => { e.stopPropagation(); onRemove(); }} className="text-zinc-600 hover:text-red-400 transition-colors p-1">
          <Trash2 size={12} />
        </button>
        {expanded ? <ChevronUp size={14} className="text-zinc-500" /> : <ChevronDown size={14} className="text-zinc-500" />}
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-white/[0.04]">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Label *</label>
              <input value={field.label} onChange={e => onUpdate("label", e.target.value)}
                className="w-full bg-transparent border border-white/10 rounded px-2.5 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none"
                placeholder="e.g. Full Name" data-testid={`field-label-${idx}`} />
            </div>
            <div>
              <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Type</label>
              <select value={field.type} onChange={e => onUpdate("type", e.target.value)}
                className="w-full bg-[#1A1A1A] border border-white/10 rounded px-2 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none"
                data-testid={`field-type-${idx}`}>
                {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Placeholder</label>
              <input value={field.placeholder || ""} onChange={e => onUpdate("placeholder", e.target.value)}
                className="w-full bg-transparent border border-white/10 rounded px-2.5 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
            </div>
            <div>
              <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Helper Text</label>
              <input value={field.helper_text || ""} onChange={e => onUpdate("helper_text", e.target.value)}
                className="w-full bg-transparent border border-white/10 rounded px-2.5 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={field.required} onChange={e => onUpdate("required", e.target.checked)} className="accent-[#C9A055]" />
            Required field
          </label>
          {needsOptions && (
            <div>
              <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Options (one per line)</label>
              <textarea
                value={(field.options || []).join("\n")}
                onChange={e => onUpdate("options", e.target.value.split("\n").filter(Boolean))}
                rows={3}
                className="w-full bg-transparent border border-white/10 rounded px-2.5 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none resize-none"
                placeholder={"Option 1\nOption 2\nOption 3"}
                data-testid={`field-options-${idx}`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══ ASSIGNMENTS LIST ═══ */
function AssignmentsList({ assignments, templates, clients, onRefresh, onView, readOnly }) {
  const [showAssign, setShowAssign] = useState(false);
  const [assignForm, setAssignForm] = useState({ template_id: "", client_id: "", due_date: "", notes: "" });
  const [assigning, setAssigning] = useState(false);

  const activeTemplates = templates.filter(t => t.active);

  const assign = async () => {
    if (!assignForm.template_id || !assignForm.client_id) { toast.error("Select a template and client"); return; }
    setAssigning(true);
    try {
      await http.post("/forms/admin/assign", assignForm);
      toast.success("Form assigned");
      setShowAssign(false);
      setAssignForm({ template_id: "", client_id: "", due_date: "", notes: "" });
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign"); }
    finally { setAssigning(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this assignment?")) return;
    try { await http.delete(`/forms/admin/assignments/${id}`); toast.success("Removed"); onRefresh(); }
    catch { toast.error("Failed"); }
  };

  const STATUS_STYLE = {
    assigned: "text-yellow-400 bg-yellow-900/20",
    in_progress: "text-blue-400 bg-blue-900/20",
    completed: "text-green-400 bg-green-900/20",
    expired: "text-red-400 bg-red-900/20",
  };

  return (
    <div className="space-y-4">
      {!readOnly && (
        <div className="flex justify-between items-center">
          <p className="text-zinc-400 text-sm">{assignments.length} pending assignment{assignments.length !== 1 ? "s" : ""}</p>
          <button onClick={() => setShowAssign(!showAssign)} data-testid="assign-form-btn"
            className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37] transition-all">
            <Send size={14} /> Assign Form
          </button>
        </div>
      )}

      {showAssign && (
        <div className="rounded-xl border border-[#C9A055]/20 bg-[#C9A055]/[0.03] p-5 space-y-4" data-testid="assign-form-panel">
          <h4 className="text-[#F4F1EA] text-sm font-medium">Assign a Form to a Client</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Template *</label>
              <select value={assignForm.template_id} onChange={e => setAssignForm(prev => ({ ...prev, template_id: e.target.value }))}
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none"
                data-testid="assign-template-select">
                <option value="">Select template...</option>
                {activeTemplates.map(t => <option key={t.id} value={t.id}>{t.title} ({t.type})</option>)}
              </select>
            </div>
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Client *</label>
              <select value={assignForm.client_id} onChange={e => setAssignForm(prev => ({ ...prev, client_id: e.target.value }))}
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none"
                data-testid="assign-client-select">
                <option value="">Select client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Due Date (optional)</label>
              <input type="date" value={assignForm.due_date} onChange={e => setAssignForm(prev => ({ ...prev, due_date: e.target.value }))}
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Notes</label>
              <input value={assignForm.notes} onChange={e => setAssignForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={assign} disabled={assigning} data-testid="confirm-assign-btn"
              className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37] disabled:opacity-50">
              {assigning ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Assign
            </button>
            <button onClick={() => setShowAssign(false)} className="px-4 py-2 text-xs text-zinc-400 hover:text-zinc-200">Cancel</button>
          </div>
        </div>
      )}

      {assignments.length === 0 && (
        <div className="text-center py-16 text-zinc-500">
          <ClipboardList size={40} className="mx-auto mb-4 opacity-30" />
          <p>{readOnly ? "No completed submissions yet." : "No forms assigned yet."}</p>
        </div>
      )}

      <div className="space-y-2">
        {assignments.map(a => (
          <div key={a.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between gap-4"
            data-testid={`assignment-${a.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLE[a.status] || "bg-zinc-800 text-zinc-400"}`}>
                  {a.status}
                </span>
                <span className="text-zinc-600 text-[10px]">{a.template_type}</span>
              </div>
              <p className="text-[#F4F1EA] text-sm font-medium truncate">{a.template_title}</p>
              <p className="text-zinc-500 text-xs">{a.client_name} · {a.client_email}</p>
              <p className="text-zinc-600 text-[10px] mt-0.5">
                Assigned {new Date(a.assigned_at).toLocaleDateString()}
                {a.due_date && ` · Due ${a.due_date}`}
                {a.completed_at && ` · Completed ${new Date(a.completed_at).toLocaleDateString()}`}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {a.status === "completed" && (
                <button onClick={() => onView(a)} className="text-zinc-500 hover:text-[#C9A055] transition-colors p-2" data-testid={`view-submission-${a.id}`}>
                  <Eye size={14} />
                </button>
              )}
              {!readOnly && (
                <button onClick={() => del(a.id)} className="text-zinc-500 hover:text-red-400 transition-colors p-2">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ SUBMISSION VIEWER ═══ */
function SubmissionViewer({ assignment, onClose }) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get(`/forms/admin/assignments/${assignment.id}`)
      .then(r => setFull(r.data))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [assignment.id]);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#C9A055]" /></div>;
  if (!full) return null;

  const template = full.template || {};
  const responses = full.responses || {};

  return (
    <div className="space-y-6" data-testid="submission-viewer">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[#F4F1EA] text-lg font-medium">{full.template_title}</h3>
          <p className="text-zinc-500 text-sm">{full.client_name} · {full.client_email}</p>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={18} /></button>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4">
        {(template.fields || []).map(field => (
          <div key={field.id} className="border-b border-white/[0.04] pb-3 last:border-0 last:pb-0">
            <p className="text-zinc-400 text-xs uppercase tracking-wider mb-1">{field.label}</p>
            <p className="text-[#F4F1EA] text-sm whitespace-pre-wrap">
              {responses[field.id] !== undefined && responses[field.id] !== null && responses[field.id] !== ""
                ? (typeof responses[field.id] === "boolean" ? (responses[field.id] ? "Yes" : "No") : String(responses[field.id]))
                : <span className="text-zinc-600 italic">Not answered</span>
              }
            </p>
          </div>
        ))}
      </div>

      {full.signature_name && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.03] p-6">
          <p className="text-amber-400 text-xs uppercase tracking-wider mb-2">Digital Signature</p>
          <p className="text-[#F4F1EA] text-xl font-serif italic">"{full.signature_name}"</p>
          <p className="text-zinc-500 text-[10px] mt-2">
            Signed {full.signed_at ? new Date(full.signed_at).toLocaleString() : "N/A"}
            {full.signature_ip && ` · IP: ${full.signature_ip}`}
          </p>
          {template.acknowledgment_text && (
            <p className="text-zinc-400 text-xs mt-3 italic">"{template.acknowledgment_text}"</p>
          )}
        </div>
      )}

      <button onClick={onClose} className="px-6 py-2.5 rounded-lg text-sm border border-white/10 text-zinc-400 hover:text-zinc-200 transition-colors">
        Close
      </button>
    </div>
  );
}
